import React, { useEffect, useMemo, useRef } from "react";
import { Label } from "src/components/ui/Label";
import { Input } from "src/components/ui/Input";
import { AutocompleteOption } from "src/components/ui/AutocompleteInput";
import AutocompleteInput from "src/components/ui/AutocompleteInput";
import {
  useCountries,
  useEducationLevels,
  useDegrees,
  useFieldsOfStudy,
  useInstitutions,
  useSearchCountries,
  useSearchEducationLevels,
  useSearchDegrees,
  useSearchFieldsOfStudy,
  useSearchInstitutions
} from "src/hooks/useEducationData";
import { TutorEducation } from "src/store/tutorOnboardingStore";

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 80 }, (_, i) => currentYear - i);

interface Props {
  index: number;
  entry: Partial<TutorEducation>;
  debouncedSearchQueries: Record<string, string>;
  updateSearchQuery: (key: string, value: string) => void;
  onChange: (index: number, field: keyof TutorEducation, value: any) => void;
  excludedLevels: string[];
  allEntries: Partial<TutorEducation>[];
}

const EducationEntryForm: React.FC<Props> = ({
  index,
  entry,
  debouncedSearchQueries,
  updateSearchQuery,
  onChange,
  excludedLevels,
  allEntries
}) => {
  const getKey = (field: string) => `${index}_${field}`;
  const previousCountryRef = useRef(entry.country);

  // Clear level when country changes
  useEffect(() => {
    if (previousCountryRef.current !== entry.country) {
      if (entry.level) {
        onChange(index, "level", "");
      }
      previousCountryRef.current = entry.country;
    }
  }, [entry.country, entry.level, index, onChange]);

  const countryQuery = debouncedSearchQueries[getKey("country")] || "";
  const levelQuery = debouncedSearchQueries[getKey("level")] || "";
  const degreeQuery = debouncedSearchQueries[getKey("degree")] || "";
  const fieldQuery = debouncedSearchQueries[getKey("field")] || "";
  const institutionQuery = debouncedSearchQueries[getKey("institution")] || "";

  const { data: allCountries, isLoading: allCountriesLoading } = useCountries();
  const { data: allLevels, isLoading: allLevelsLoading } = useEducationLevels(entry.country);
  const { data: allDegrees, isLoading: allDegreesLoading } = useDegrees({ country: entry.country, level: entry.level });
  const { data: allFields, isLoading: allFieldsLoading } = useFieldsOfStudy({ country: entry.country });
  const { data: allInstitutions, isLoading: allInstitutionsLoading } = useInstitutions(entry.country);

  const { data: searchedCountries, isLoading: searchCountriesLoading } = useSearchCountries(countryQuery);
  const { data: searchedLevels, isLoading: searchLevelsLoading } = useSearchEducationLevels(levelQuery, entry.country);
  const { data: searchedDegrees, isLoading: searchDegreesLoading } = useSearchDegrees(degreeQuery, {
    country: entry.country,
    level: entry.level
  });
  const { data: searchedFields, isLoading: searchFieldsLoading } = useSearchFieldsOfStudy(fieldQuery, {
    country: entry.country
  });
  const { data: searchedInstitutions, isLoading: searchInstitutionsLoading } = useSearchInstitutions(institutionQuery, entry.country);

  const countries = countryQuery.length >= 1 ? searchedCountries : allCountries;
  
  // Get levels and filter out excluded ones
  const rawLevels = (levelQuery.length >= 1 && entry.country) ? searchedLevels : (entry.country ? allLevels : []);
  const levels = rawLevels?.filter(l => !excludedLevels.includes(l.name) || l.name === entry.level) || [];
  
  const degrees = degreeQuery.length >= 2 ? searchedDegrees : allDegrees;
  const fields = fieldQuery.length >= 2 ? searchedFields : allFields;
  const institutions = institutionQuery.length >= 2 ? searchedInstitutions : allInstitutions;

  const countriesLoading = countryQuery.length >= 1 ? searchCountriesLoading : allCountriesLoading;
  const levelsLoading = (levelQuery.length >= 1 && entry.country) ? searchLevelsLoading : (entry.country ? allLevelsLoading : false);
  const degreesLoading = degreeQuery.length >= 2 ? searchDegreesLoading : allDegreesLoading;
  const fieldsLoading = fieldQuery.length >= 2 ? searchFieldsLoading : allFieldsLoading;
  const institutionsLoading = institutionQuery.length >= 2 ? searchInstitutionsLoading : allInstitutionsLoading;

  const countryOptions: AutocompleteOption[] = countries?.map(c => ({ id: c.code, label: c.name, value: c.name })) || [];
  const levelOptions: AutocompleteOption[] = levels?.map(l => ({ id: l.id, label: l.name, value: l.name })) || [];
  const degreeOptions: AutocompleteOption[] = degrees?.map(d => ({ id: d.id, label: d.display_name, value: d.name })) || [];
  const fieldOptions: AutocompleteOption[] = fields?.map(f => ({ id: f.id, label: f.display_name, value: f.name })) || [];
  const institutionOptions: AutocompleteOption[] = institutions?.map(i => ({
    id: i.id,
    label: i.name,
    value: i.name,
    description: `${i.region}${i.is_verified ? " • Verified" : ""}`
  })) || [];

  // Check if GPA is greater than Total GPA
  const isGpaGreaterThanTotal = useMemo(() => {
    if (entry.gpa && entry.gpa_total) {
      return entry.gpa > entry.gpa_total;
    }
    return false;
  }, [entry.gpa, entry.gpa_total]);

  // Education level ranking (higher number = higher level)
  const getLevelRank = (level: string): number => {
    const levelRanks: { [key: string]: number } = {
      'Doctoral': 6,
      'Postgraduate diploma': 5,
      'Graduate': 4,
      'Undergraduate': 3,
      'Post diploma': 2,
      'Diploma': 1
    };
    return levelRanks[level] || 0;
  };

  // Check if current level violates the ordering (should be highest to lowest)
  const levelOrderingError = useMemo(() => {
    if (!entry.level) return null;

    const currentLevelRank = getLevelRank(entry.level);
    
    // Check previous entries (should have higher or equal level)
    for (let i = 0; i < index; i++) {
      const prevEntry = allEntries[i];
      if (prevEntry.level) {
        const prevLevelRank = getLevelRank(prevEntry.level);
        if (currentLevelRank > prevLevelRank) {
          return {
            type: 'higher_than_previous',
            previousLevel: prevEntry.level,
            previousIndex: i
          };
        }
      }
    }

    // Check next entries (should have lower or equal level)
    for (let i = index + 1; i < allEntries.length; i++) {
      const nextEntry = allEntries[i];
      if (nextEntry.level) {
        const nextLevelRank = getLevelRank(nextEntry.level);
        if (currentLevelRank < nextLevelRank) {
          return {
            type: 'lower_than_next',
            nextLevel: nextEntry.level,
            nextIndex: i
          };
        }
      }
    }

    return null;
  }, [entry.level, allEntries, index]);

  return (
    <div className="bg-white border rounded-2xl p-5 shadow-sm">
      {/* Education Level Ordering Error Message */}
      {levelOrderingError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
                <span className="font-medium">Education level order issue:</span>{' '}
                {levelOrderingError.type === 'higher_than_previous' ? (
                  <>
                    Your current level "{entry.level}" is higher than Education #{levelOrderingError.previousIndex + 1} ("{levelOrderingError.previousLevel}"). 
                    Please arrange your education from highest to lowest level (most recent first).
                  </>
                ) : (
                  <>
                    Your current level "{entry.level}" is lower than Education #{levelOrderingError.nextIndex + 1} ("{levelOrderingError.nextLevel}"). 
                    Please arrange your education from highest to lowest level (most recent first).
                  </>
                )}
              </p>
              <p className="text-xs text-red-600 mt-1" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
                Level ranking: Doctoral → Postgraduate diploma → Graduate → Undergraduate → Post diploma → Diploma
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Friendly message when GPA is greater than Total GPA */}
      {isGpaGreaterThanTotal && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-800" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
                <span className="font-medium">Just a heads up!</span> Your GPA ({entry.gpa}) seems to be higher than your total GPA scale ({entry.gpa_total}). You might want to double-check these values.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-sm font-medium inline-flex items-center" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
            Country<span className="text-red-500 ml-1">*</span>
          </Label>
          <AutocompleteInput
            label=""
            placeholder="e.g. United States"
            value={entry.country || ""}
            onChange={value => {
              onChange(index, "country", value);
              if (!value) {
                onChange(index, "level", "");
                onChange(index, "degree", "");
                onChange(index, "field", "");
                onChange(index, "institution", "");
              }
            }}
            options={countryOptions}
            isLoading={countriesLoading}
            onInputChange={query => updateSearchQuery(getKey("country"), query)}
          />
        </div>

        <div>
          <Label className="text-sm font-medium inline-flex items-center" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
            Education Level<span className="text-red-500 ml-1">*</span>
          </Label>
          <AutocompleteInput
            label=""
            placeholder={entry.country ? "e.g. Bachelor's" : "Please select a country first"}
            value={entry.level || ""}
            onChange={value => {
              onChange(index, "level", value);
            }}
            options={levelOptions}
            isLoading={levelsLoading}
            onInputChange={query => updateSearchQuery(getKey("level"), query)}
            disabled={!entry.country}
          />
        </div>

        <div>
          <Label className="text-sm font-medium inline-flex items-center" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
            Degree<span className="text-red-500 ml-1">*</span>
          </Label>
          <AutocompleteInput
            label=""
            placeholder="e.g. Bachelor of Science"
            value={entry.degree || ""}
            onChange={value => {
              onChange(index, "degree", value);
            }}
            options={degreeOptions}
            isLoading={degreesLoading}
            onInputChange={query => updateSearchQuery(getKey("degree"), query)}
            disabled={!entry.country}
          />
        </div>

        <div>
          <Label className="text-sm font-medium inline-flex items-center" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
            Field of Study<span className="text-red-500 ml-1">*</span>
          </Label>
          <AutocompleteInput
            label=""
            placeholder="e.g. Computer Science"
            value={entry.field || ""}
            onChange={value => {
              onChange(index, "field", value);
            }}
            options={fieldOptions}
            isLoading={fieldsLoading}
            onInputChange={query => updateSearchQuery(getKey("field"), query)}
            disabled={!entry.country}
          />
        </div>

        <div>
          <Label className="text-sm font-medium inline-flex items-center" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
            Institution<span className="text-red-500 ml-1">*</span>
          </Label>
          <AutocompleteInput
            label=""
            placeholder="e.g. Stanford University"
            value={entry.institution || ""}
            onChange={value => {
              onChange(index, "institution", value);
            }}
            options={institutionOptions}
            isLoading={institutionsLoading}
            onInputChange={query => updateSearchQuery(getKey("institution"), query)}
            disabled={!entry.country}
          />
        </div>

        <div>
          <Label className="text-sm font-medium inline-flex items-center" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
            Year of Completion<span className="text-red-500 ml-1">*</span>
          </Label>
          <select
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
            value={entry.year_of_completion || ""}
            onChange={(e) => onChange(index, "year_of_completion", parseInt(e.target.value))}
            style={{ fontFamily: 'Segoe UI, sans-serif' }}
          >
            <option value="">Select year</option>
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-sm font-medium" style={{ fontFamily: 'Segoe UI, sans-serif' }}>GPA (Optional)</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="e.g. 3.8"
            value={entry.gpa || ""}
            onChange={(e) => onChange(index, "gpa", parseFloat(e.target.value))}
            style={{ fontFamily: 'Segoe UI, sans-serif' }}
          />
        </div>

        <div>
          <Label className="text-sm font-medium" style={{ fontFamily: 'Segoe UI, sans-serif' }}>Total GPA (Optional)</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="e.g. 4.0"
            value={entry.gpa_total || ""}
            onChange={(e) => onChange(index, "gpa_total", parseFloat(e.target.value))}
            style={{ fontFamily: 'Segoe UI, sans-serif' }}
          />
        </div>

        <div className="col-span-2">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={entry.is_custom_degree || false}
              onChange={(e) => onChange(index, "is_custom_degree", e.target.checked)}
            />
            <span className="text-sm" style={{ fontFamily: 'Segoe UI, sans-serif' }}>This is a custom/non-standard degree</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default EducationEntryForm;
